import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import { Send01 } from "@velaro/velaro-component-lib";
import useTheme from "@/hooks/useTheme";
import type { AdminTheme } from "@/context/ThemeContext";
import {
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  IconButton
} from "@/components/ds";
import {
  Sparkles,
  Bot,
  MessageSquare,
  Code,
  ShoppingCart,
  BookOpen,
  User,
  Plus,
  Send,
  HelpCircle,
  Lightbulb,
  Check,
  RefreshCw,
  Wand,
  Star,
  Search,
  Copy,
  Mail,
  X
} from "@/components/ds/icons";

type IconComponent = React.ComponentType<{ className?: string }>;

// ── Types ─────────────────────────────────────────────────────────────────

interface WorkflowProposal {
  name: string;
  trigger_type: string;
  nodes_blob: string;
  description: string;
  steps: string[];
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  proposal?: WorkflowProposal;
  showRating?: boolean;
  ratingDismissed?: boolean;
}

interface TicketForm {
  subject: string;
  description: string;
  contactName: string;
  contactEmail: string;
}

interface TicketResult {
  ticketNumber: string;
  id: number;
}

interface NearLimit {
  feature: string;
  current: number;
  max: number;
  percentUsed: number;
}

interface SubscriptionStatus {
  enabledFeatures: string[];
  disabledFeatures: Array<{
    flag: string;
    name: string;
    description: string;
    package: string;
    learnMorePath: string;
  }>;
  nearLimits: NearLimit[];
  usage: Record<string, number | string>;
  summary: string;
}

interface LogEntry {
  id: number;
  integration: string;
  severity: "Info" | "Warning" | "Error";
  category: string;
  message: string;
  additionalInfo?: string;
  suppressedCount: number;
  createdAt: number;
}

interface LogPage {
  total: number;
  page: number;
  pageSize: number;
  items: LogEntry[];
}

interface Props {
  /** Current page context sent to the AI to tailor responses (e.g. "workflows", "integrations/bigcommerce") */
  context?: string;
  /** Integration key (e.g. "NetSuite", "HubSpot") — triggers fetching recent errors for proactive diagnosis */
  integrationKey?: string;
  /** Pre-fills the input field so the user can review before sending (e.g. from workflow test transcript) */
  prefillMessage?: string;
  /** Message injected directly as an assistant message on open — no user send needed (e.g. guided workflow context) */
  autoMessage?: string;
  /** Called when the user closes the drawer */
  onClose: () => void;
}

type Product = "v20" | "v10";

const PRODUCT_LABELS: Record<Product, string> = {
  v20: "Velaro Admin (v20)",
  v10: "Velaro Console (v10)"
};

// ── Frustration detection ─────────────────────────────────────────────────
// Client-side keyword matching. Intentionally broad — false positives just
// show a polite nudge, not an error. Real sentiment API can replace this later.

const FRUSTRATION_PHRASES = [
  "frustrated",
  "frustrating",
  "doesn't work",
  "doesn't help",
  "not working",
  "not helpful",
  "wrong answer",
  "incorrect",
  "useless",
  "terrible",
  "horrible",
  "hate this",
  "still stuck",
  "still doesn't",
  "still not",
  "keeps failing",
  "can't figure",
  "can't do",
  "can't get",
  "cannot do",
  "cannot get",
  "giving up",
  "this is broken",
  "this is bad",
  "waste of time",
  "no help",
  "not helping",
  "didn't answer",
  "didn't help",
  "doesn't answer",
  "wrong",
  "confused",
  "confusing"
];

function detectFrustration(text: string): boolean {
  const lower = text.toLowerCase();
  return FRUSTRATION_PHRASES.some((p) => lower.includes(p));
}

// ── Quick-reply detection ─────────────────────────────────────────────────
// Pure client-side. Only runs against the LAST assistant message.
function detectQuickReplies(content: string): string[] {
  if (!content) return [];
  const lower = content.toLowerCase();
  const trimmed = content.trim();
  if (!trimmed.endsWith("?")) return [];
  if (
    /\b(ready to|go ahead|proceed|confirm|sound good|look good)\b/.test(lower)
  ) {
    return ["Yes, go ahead", "Not yet"];
  }
  if (
    /\b(are you|do you want|should i|would you like|shall i|is that|does that|can i|want me to)\b/.test(
      lower
    )
  ) {
    return ["Yes", "No"];
  }
  return [];
}

// ── Quick-start suggestion chips ─────────────────────────────────────────

interface Suggestion {
  Icon: IconComponent;
  label: string;
  query: string;
}

const BASE_SUGGESTIONS: Suggestion[] = [
  {
    Icon: Sparkles,
    label: "Set up a workflow",
    query: "How do I set up a workflow?"
  },
  { Icon: Bot, label: "AI automation", query: "How does AI automation work?" },
  {
    Icon: HelpCircle,
    label: "Ticket workflow",
    query: "How do I create a support ticket workflow?"
  },
  {
    Icon: MessageSquare,
    label: "Channels overview",
    query: "What channels does Velaro support?"
  },
  {
    Icon: Code,
    label: "Variables guide",
    query: "How do I use variables in workflows?"
  }
];

/** Build context-aware suggestions based on enabled features */
function buildSuggestions(enabledFeatures?: string[]): Suggestion[] {
  const extras: Suggestion[] = [];
  const enabled = new Set(enabledFeatures ?? []);
  if (enabled.has("BigCommerce Integration"))
    extras.push({
      Icon: ShoppingCart,
      label: "BigCommerce setup",
      query: "How do I set up BigCommerce?"
    });
  if (enabled.has("Shopify Integration"))
    extras.push({
      Icon: ShoppingCart,
      label: "Shopify setup",
      query: "How do I connect Shopify?"
    });
  if (enabled.has("HubSpot Integration"))
    extras.push({
      Icon: User,
      label: "HubSpot setup",
      query: "How do I connect HubSpot?"
    });
  if (enabled.has("Knowledge Base"))
    extras.push({
      Icon: BookOpen,
      label: "Knowledge Base",
      query: "How do I set up the Knowledge Base?"
    });
  // Show max 6 total — base + relevant extras
  return [...BASE_SUGGESTIONS, ...extras].slice(0, 6);
}

// ── Session persistence helpers ─────────────────────────────────────────

const SESSION_KEY = "copilot-messages";
const THEME_KEY = "copilot-theme";

function loadMessages(): ChatMessage[] {
  try {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    /* ignore */
  }
  // Origin story is told only when a user explicitly asks "why Moshky?" (handled server-side
  // in CopilotController.GetDefaultSystemPromptHeader) — never as an unprompted greeting.
  return [{ role: "assistant", content: "Hi! What can I help you with?" }];
}

function saveMessages(msgs: ChatMessage[]) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(msgs.slice(-50)));
  } catch {
    /* ignore */
  }
}

function loadTheme(): "dark" | "light" {
  try {
    return (localStorage.getItem(THEME_KEY) as "dark" | "light") ?? "light";
  } catch {
    return "light";
  }
}

function copyToClipboard(text: string): Promise<void> {
  return navigator.clipboard.writeText(text);
}

// ── Markdown → simple HTML (lightweight, no dependency) ──────────────────

function renderMarkdown(text: string, isDark = true): string {
  const boldColor = isDark ? "text-white" : "text-cornflower-blue-700";
  const headingColor = isDark ? "text-white" : "text-zinc-900";
  // Protect code blocks first (extract before escaping)
  const codeBlocks: string[] = [];
  const inlineCodes: string[] = [];
  let s = text
    .replace(/```([\s\S]*?)```/g, (_, inner) => {
      const code = inner.replace(/^\w*\n/, "");
      codeBlocks.push(
        `<pre class="bg-zinc-900 text-green-300 rounded-lg p-3 text-xs my-2 overflow-x-auto whitespace-pre-wrap border border-zinc-700"><code>${inner.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</code></pre>`
      );
      return `\x00CODE${codeBlocks.length - 1}\x00`;
    })
    .replace(/`([^`]+)`/g, (_, inner) => {
      inlineCodes.push(
        `<code class="bg-zinc-700 text-green-300 rounded px-1 text-xs">${inner.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</code>`
      );
      return `\x00IC${inlineCodes.length - 1}\x00`;
    });

  // Escape HTML in the rest
  s = s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  s = s
    // Navigation links [→ label](/path)
    .replace(
      /\[→\s*([^\]]+)\]\(([^)]+)\)/g,
      '<a class="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 underline cursor-pointer font-medium" data-nav="$2">→ $1</a>'
    )
    // Regular markdown links [label](url)
    .replace(
      /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
      '<a class="text-blue-400 hover:text-blue-300 underline" href="$2" target="_blank" rel="noreferrer">$1</a>'
    )
    // H3 headings (### text)
    .replace(
      /^###\s+(.+)$/gm,
      `<p class="${headingColor} font-semibold text-sm mt-3 mb-1">$1</p>`
    )
    // H2 headings (## text)
    .replace(
      /^##\s+(.+)$/gm,
      `<p class="${headingColor} font-bold text-sm mt-3 mb-1">$1</p>`
    )
    // Bold
    .replace(
      /\*\*([^*]+)\*\*/g,
      `<strong class="font-semibold ${boldColor}">$1</strong>`
    )
    // Italic
    .replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>')
    // Numbered lists
    .replace(
      /^(\d+)\.\s+(.+)$/gm,
      '<div class="flex gap-2 my-0.5"><span class="text-blue-400 font-semibold min-w-[18px]">$1.</span><span>$2</span></div>'
    )
    // Bullet lists
    .replace(
      /^[-*]\s+(.+)$/gm,
      '<div class="flex gap-2 my-0.5"><span class="text-zinc-400">•</span><span>$1</span></div>'
    )
    // Paragraphs
    .replace(/\n\n/g, '<div class="h-2"></div>')
    .replace(/\n/g, "<br/>");

  // Restore code blocks
  s = s.replace(/\x00CODE(\d+)\x00/g, (_, i) => codeBlocks[+i]);
  s = s.replace(/\x00IC(\d+)\x00/g, (_, i) => inlineCodes[+i]);
  return s;
}

// ── Workflow Proposal Card ────────────────────────────────────────────────

interface WorkflowProposalCardProps {
  proposal: WorkflowProposal;
  isDark: boolean;
  msgIndex: number;
  onApply: (proposal: WorkflowProposal, idx: number) => void;
  onCopySteps: (steps: string[], idx: number) => void;
  applying: boolean;
  applied: boolean;
}

function WorkflowProposalCard({
  proposal,
  isDark,
  msgIndex,
  onApply,
  onCopySteps,
  applying,
  applied
}: WorkflowProposalCardProps) {
  const cardBg = isDark
    ? "bg-zinc-700/70 border-zinc-500"
    : "bg-blue-50 border-blue-200";
  const labelColor = isDark ? "text-blue-300" : "text-blue-700";
  const nameColor = isDark
    ? "text-white font-semibold"
    : "text-zinc-900 font-semibold";
  const descColor = isDark ? "text-zinc-300" : "text-zinc-600";
  const trigColor = isDark ? "text-zinc-400" : "text-zinc-500";

  return (
    <div className={`mt-2 rounded-xl border p-3 text-sm ${cardBg}`}>
      <div
        className={`text-[11px] font-semibold uppercase tracking-wide mb-1.5 flex items-center gap-1.5 ${labelColor}`}
      >
        <Sparkles className="w-3.5 h-3.5" />
        Proposed Workflow
      </div>
      <div className={nameColor}>{proposal.name}</div>
      <div className={`text-xs mt-0.5 ${trigColor}`}>
        Trigger: <span className="font-medium">{proposal.trigger_type}</span>
      </div>
      {proposal.description && (
        <p className={`text-xs mt-2 leading-relaxed ${descColor}`}>
          {proposal.description}
        </p>
      )}
      <div className="flex gap-2 mt-3">
        <PrimaryButton
          onClick={() => onApply(proposal, msgIndex)}
          disabled={applying || applied}
          className={`!flex-1 !text-xs !rounded-lg !px-3 !py-2 !gap-1.5 ${
            applied
              ? isDark
                ? "!bg-green-900/60 !text-green-300 border border-green-700 !cursor-default disabled:!opacity-100"
                : "!bg-green-100 !text-green-700 border border-green-300 !cursor-default disabled:!opacity-100"
              : isDark
                ? "!bg-blue-600 hover:!bg-blue-500 !text-white"
                : "!bg-blue-600 hover:!bg-blue-700 !text-white"
          }`}
        >
          {applied ? (
            <>
              <Check className="w-3.5 h-3.5" /> Created
            </>
          ) : applying ? (
            <>
              <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Creating…
            </>
          ) : (
            <>
              <Wand className="w-3.5 h-3.5" /> Do it for me
            </>
          )}
        </PrimaryButton>
        <SecondaryButton
          onClick={() => onCopySteps(proposal.steps, msgIndex)}
          disabled={applied}
          className={`!text-xs !rounded-lg !px-3 !py-2 border !bg-transparent !gap-1.5 disabled:!opacity-40 ${
            isDark
              ? "!border-zinc-500 !text-zinc-300 hover:!bg-zinc-600 hover:!text-white"
              : "!border-zinc-300 !text-zinc-600 hover:!bg-zinc-100 hover:!text-zinc-900"
          }`}
          title="Copy manual steps to clipboard"
        >
          <Check className="w-3.5 h-3.5" /> Show steps
        </SecondaryButton>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────

export default function CopilotAssistant({
  context = "",
  integrationKey,
  prefillMessage,
  autoMessage,
  onClose
}: Props) {
  const api = useApi();
  const navigate = useNavigate();
  const { theme: adminTheme, setTheme: setAdminTheme } = useTheme();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // ── Panel size state (resizable) ──────────────────────────────────────────
  const [panelSize, setPanelSize] = useState<{ w: number; h: number }>(() => {
    try {
      const s = localStorage.getItem("copilot-admin-size");
      if (s) {
        const p = JSON.parse(s);
        if (p.w && p.h) return p;
      }
    } catch {
      /* ignore */
    }
    return { w: 400, h: 580 };
  });
  const panelSizeRef = useRef(panelSize);

  // ── Drag state — pointer-event approach, direct DOM writes for smooth 60fps ──
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>(() => {
    try {
      const saved = localStorage.getItem("copilot-admin-pos");
      return saved ? JSON.parse(saved) : { x: 0, y: 0 };
    } catch {
      return { x: 0, y: 0 };
    }
  });
  const dragOffsetRef = useRef(dragOffset);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ mx: 0, my: 0, ox: 0, oy: 0 });

  const onHeaderPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    isDraggingRef.current = true;
    dragStartRef.current = {
      mx: e.clientX,
      my: e.clientY,
      ox: dragOffsetRef.current.x,
      oy: dragOffsetRef.current.y
    };
  };
  const onHeaderPointerMove = (e: React.PointerEvent) => {
    if (!isDraggingRef.current) return;
    const { mx, my, ox, oy } = dragStartRef.current;
    const rawX = ox + e.clientX - mx;
    const rawY = oy + e.clientY - my;
    const w = panelSizeRef.current.w;
    const h = panelSizeRef.current.h;
    const clampedX = Math.max(
      -(window.innerWidth - w - 20),
      Math.min(rawX, window.innerWidth - 20 - w)
    );
    const clampedY = Math.max(
      -(window.innerHeight - h - 80),
      Math.min(rawY, 40)
    );
    const next = { x: clampedX, y: clampedY };
    dragOffsetRef.current = next;
    if (panelRef.current) {
      panelRef.current.style.transform = `translate(${next.x}px, ${next.y}px)`;
    }
  };
  const onHeaderPointerUp = () => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setDragOffset({ ...dragOffsetRef.current });
    try {
      localStorage.setItem(
        "copilot-admin-pos",
        JSON.stringify(dragOffsetRef.current)
      );
    } catch {
      /* ignore */
    }
  };

  // ── Resize state (bottom-right corner) ────────────────────────────────────
  const isResizingRef = useRef(false);
  const resizeStartRef = useRef({ mx: 0, my: 0, ow: 0, oh: 0 });

  const onResizePointerDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    isResizingRef.current = true;
    resizeStartRef.current = {
      mx: e.clientX,
      my: e.clientY,
      ow: panelSizeRef.current.w,
      oh: panelSizeRef.current.h
    };
  };
  const onResizePointerMove = (e: React.PointerEvent) => {
    if (!isResizingRef.current) return;
    const { mx, my, ow, oh } = resizeStartRef.current;
    const newW = Math.max(
      320,
      Math.min(ow + e.clientX - mx, window.innerWidth - 40)
    );
    const newH = Math.max(
      400,
      Math.min(oh + e.clientY - my, window.innerHeight - 80)
    );
    const next = { w: newW, h: newH };
    panelSizeRef.current = next;
    if (panelRef.current) {
      panelRef.current.style.width = newW + "px";
      panelRef.current.style.height = newH + "px";
    }
  };
  const onResizePointerUp = () => {
    if (!isResizingRef.current) return;
    isResizingRef.current = false;
    setPanelSize({ ...panelSizeRef.current });
    try {
      localStorage.setItem(
        "copilot-admin-size",
        JSON.stringify(panelSizeRef.current)
      );
    } catch {
      /* ignore */
    }
  };

  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    // If an autoMessage is provided, start fresh with just that message (don't restore session)
    if (autoMessage) {
      return [{ role: "assistant", content: autoMessage }];
    }
    return loadMessages();
  });
  const [input, setInput] = useState(prefillMessage ?? "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Theme + expand
  const [theme, setTheme] = useState<"dark" | "light">(loadTheme);
  const [expanded, setExpanded] = useState(false);

  // Copy feedback
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  // Workflow proposal card state
  const [proposalApplying, setProposalApplying] = useState<number | null>(null);
  const [proposalApplied, setProposalApplied] = useState<Set<number>>(
    new Set()
  );

  // Preferred email for Moshky email responses (persisted in localStorage)
  const [preferredEmail, setPreferredEmail] = useState<string>(() => {
    try {
      return localStorage.getItem("copilot-preferred-email") ?? "";
    } catch {
      return "";
    }
  });
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [pendingEmailContent, setPendingEmailContent] = useState<string | null>(
    null
  );
  const [emailPromptValue, setEmailPromptValue] = useState("");

  // NPS rating
  const [showRating, setShowRating] = useState(false);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [ratingSent, setRatingSent] = useState(false);
  const [sendingRating, setSendingRating] = useState(false);
  const messageCountSinceRating = useRef(0);

  // Frustration detection
  const [frustrationCount, setFrustrationCount] = useState(0);
  const [showFrustrationBanner, setShowFrustrationBanner] = useState(false);
  const [feedbackDismissed, setFeedbackDismissed] = useState(false);

  // Feedback form state (lightweight — no ticket, just "what couldn't you do?")
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);

  // Integration log context — injected into every chat request when on an integration page
  const [integrationLogContext, setIntegrationLogContext] = useState("");

  // Subscription status (fetched once on open — used for near-limit banners)
  const [subStatus, setSubStatus] = useState<SubscriptionStatus | null>(null);
  const [nearLimitDismissed, setNearLimitDismissed] = useState(false);
  const [sendingUpgrade, setSendingUpgrade] = useState<string | null>(null);
  const [upgradeSent, setUpgradeSent] = useState<string | null>(null);

  // Escalation flow state
  const [escalateStep, setEscalateStep] = useState<
    "idle" | "searching" | "suggested" | "ticket"
  >("idle");
  const [escalateSuggestion, setEscalateSuggestion] = useState<string | null>(
    null
  );
  const [ticketForm, setTicketForm] = useState<TicketForm>({
    subject: "",
    description: "",
    contactName: "",
    contactEmail: ""
  });
  const [ticketResult, setTicketResult] = useState<TicketResult | null>(null);
  const [submittingTicket, setSubmittingTicket] = useState(false);
  const [product, setProduct] = useState<Product>("v20");

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Persist messages to sessionStorage
  useEffect(() => {
    saveMessages(messages);
  }, [messages]);

  // Persist theme to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  // Prompt NPS rating inline after every 5 user messages (once per session).
  // Inject a marker assistant message instead of switching to the fullscreen rating view.
  useEffect(() => {
    const userMsgCount = messages.filter((m) => m.role === "user").length;
    const alreadyHasInlineRating = messages.some((m) => m.showRating);
    if (
      userMsgCount > 0 &&
      userMsgCount % 5 === 0 &&
      !ratingSent &&
      !alreadyHasInlineRating &&
      !loading
    ) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "", showRating: true }
      ]);
    }
  }, [messages, ratingSent, loading]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Mount diagnostic — confirms component loaded and logs context
  useEffect(() => {
    console.log("[Copilot] CopilotAssistant mounted", { context, product });
  }, []);

  // Fetch subscription status once on open (silent — used for near-limit banners and upgrade prompts)
  useEffect(() => {
    let cancelled = false;
    console.log("[Copilot] Fetching SubscriptionStatus...");
    api.admin
      .get("Copilot/SubscriptionStatus")
      .then((res) => {
        console.log("[Copilot] SubscriptionStatus HTTP", res.status, res.ok);
        return res.json();
      })
      .then((data: SubscriptionStatus) => {
        console.log("[Copilot] SubscriptionStatus loaded", {
          enabled: data?.enabledFeatures?.length,
          disabled: data?.disabledFeatures?.length
        });
        if (!cancelled) setSubStatus(data);
      })
      .catch((err) => {
        console.error("[Copilot] SubscriptionStatus fetch failed:", err);
        /* non-critical — just won't show banners */
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  // Fetch recent integration errors on open — used to give proactive, specific fix advice.
  // Surfaced at most once per integration per browser session — re-opening the drawer on the
  // same integration page must not re-append the same "I can see N recent errors" banner.
  useEffect(() => {
    if (!integrationKey) return;
    const shownKey = `moshky-errors-shown-${integrationKey}`;
    if (sessionStorage.getItem(shownKey)) return;
    let cancelled = false;
    api.messaging
      .get(
        `Integration/Logs/${encodeURIComponent(integrationKey)}?severity=Error&pageSize=8`
      )
      .then((res) => res.json())
      .then((data: LogPage) => {
        if (cancelled) return;
        const actionable = (data?.items ?? []).filter(
          (l) => l.severity === "Error" || l.severity === "Warning"
        );
        if (!actionable.length) return;
        sessionStorage.setItem(shownKey, "1");

        // Build a compact context string for the AI
        const lines = actionable.map((l) => {
          const extra = l.additionalInfo ? ` — ${l.additionalInfo}` : "";
          const suppressed =
            l.suppressedCount > 0 ? ` (×${l.suppressedCount + 1})` : "";
          return `[${l.severity}] ${l.category}: ${l.message}${extra}${suppressed}`;
        });
        setIntegrationLogContext(
          `\n\n## Live ${integrationKey} Integration Diagnostics\n` +
            `The following errors were recorded by Velaro's integration logger for this site:\n` +
            lines.join("\n") +
            `\n\nUse these errors to give the user specific, actionable steps to fix the issue. ` +
            `Reference permission names, OAuth settings, or config fields exactly as they appear in NetSuite/HubSpot/Dynamics/etc.`
        );

        // Proactively surface the errors so the user knows the Copilot has seen them
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              `I can see ${actionable.length === 1 ? "a recent error" : `${actionable.length} recent errors`} ` +
              `in your **${integrationKey}** integration logs. ` +
              `Would you like me to walk you through fixing ${actionable.length === 1 ? "it" : "them"}? ` +
              `Or ask me anything and I'll factor in what I'm seeing.`
          }
        ]);
      })
      .catch(() => {
        /* non-critical — copilot still works without log context */
      });
    return () => {
      cancelled = true;
    };
  }, [integrationKey, api]);

  const buildSessionSummary = useCallback(
    () =>
      messages
        .slice(-10)
        .map((m) => `${m.role === "user" ? "User" : "Copilot"}: ${m.content}`)
        .join("\n\n"),
    [messages]
  );

  const sendMessage = useCallback(
    async (userText: string) => {
      const text = userText.trim();
      if (!text || loading) return;

      setInput("");
      setError(null);

      const newMessages: ChatMessage[] = [
        ...messages,
        { role: "user", content: text }
      ];
      setMessages(newMessages);
      setLoading(true);

      // Client-side frustration detection
      if (detectFrustration(text)) {
        setFrustrationCount((c) => {
          const next = c + 1;
          if (next >= 1 && !feedbackDismissed) setShowFrustrationBanner(true);
          return next;
        });

        // Fire-and-forget background report to server (no modal, silent)
        try {
          const summary = newMessages
            .slice(-10)
            .map(
              (m) => `${m.role === "user" ? "User" : "Copilot"}: ${m.content}`
            )
            .join("\n\n");
          api.admin
            .post("Copilot/Feedback", {
              feedbackText: text,
              sessionSummary: summary,
              triggerSource: "frustration_detected",
              pageContext: context
            })
            .catch(() => {
              /* silent */
            });
        } catch {
          /* silent */
        }
      }

      // 75-second client-side timeout — prevents infinite spinner if the server
      // hangs (Anthropic slow, DB lock, etc.). 75s > the server's 2×25s max budget.
      const abortCtrl = new AbortController();
      const abortTimer = setTimeout(() => abortCtrl.abort(), 75_000);

      try {
        const fullContext = context + integrationLogContext;
        console.log("[Copilot] Sending chat request", {
          msgCount: newMessages.length,
          context,
          hasIntegrationLogs: !!integrationLogContext
        });
        const res = await api.admin.post(
          "Copilot/Chat",
          { messages: newMessages, context: fullContext },
          abortCtrl.signal
        );
        clearTimeout(abortTimer);
        console.log("[Copilot] HTTP response status:", res.status, res.ok);

        let data: any = null;
        try {
          data = await res.json();
        } catch (parseErr) {
          console.error("[Copilot] Failed to parse JSON response:", parseErr);
          throw new Error("Invalid JSON response from server");
        }

        console.log("[Copilot] Response data:", {
          success: data?.success,
          replyLen: data?.reply?.length ?? 0,
          error: data?.error,
          reply: data?.reply
        });

        if (!res.ok) {
          console.error("[Copilot] Non-2xx HTTP status:", res.status, data);
        }

        if (data?.success === false) {
          setError(data.error ?? "Something went wrong.");
          setMessages([
            ...newMessages,
            {
              role: "assistant",
              content:
                data.reply ??
                "I'm having trouble right now. Please try again or create a support ticket."
            }
          ]);
        } else {
          // Build the assistant message, attaching any workflow proposal
          const assistantMsg: ChatMessage = {
            role: "assistant",
            content: data?.reply ?? ""
          };
          const proposalAction = Array.isArray(data?.clientActions)
            ? (data.clientActions as any[]).find(
                (a) => a.type === "workflow_proposal"
              )
            : null;
          if (proposalAction) {
            try {
              assistantMsg.proposal = JSON.parse(proposalAction.payload);
            } catch {
              /* malformed — skip */
            }
          }
          setMessages([...newMessages, assistantMsg]);

          // Execute other client-side actions (theme switch, navigate, etc.)
          if (Array.isArray(data?.clientActions)) {
            for (const action of data.clientActions) {
              if (action.type === "set_theme") {
                setAdminTheme(action.payload as AdminTheme);
              }
            }
          }
        }
      } catch (err: any) {
        clearTimeout(abortTimer);
        const timedOut = err?.name === "AbortError";
        console.error("[Copilot] sendMessage caught error:", err);
        setError(
          timedOut
            ? "The AI took too long to respond. Please try again."
            : "Connection error. Please check your network and try again."
        );
        setMessages([
          ...newMessages,
          {
            role: "assistant",
            content: timedOut
              ? "That took longer than expected. Please try again — I'm usually much faster. You can also reach support at **support@velaro.com**."
              : "I'm having trouble connecting. You can also reach support at **support@velaro.com**."
          }
        ]);
      } finally {
        clearTimeout(abortTimer);
        setLoading(false);
        // Restore focus so user can type the next message immediately
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    },
    [api, messages, context, integrationLogContext, loading, feedbackDismissed]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
    // Shift+Enter inserts a newline (default textarea behavior — let it through)
  };

  const handleApplyProposal = useCallback(
    async (proposal: WorkflowProposal, msgIndex: number) => {
      setProposalApplying(msgIndex);
      try {
        const res = await api.admin.post("Copilot/ApplyWorkflowProposal", {
          name: proposal.name,
          triggerType: proposal.trigger_type,
          nodesBlob: proposal.nodes_blob,
          description: proposal.description
        });
        if (res.ok) {
          setProposalApplied((prev) => new Set([...prev, msgIndex]));
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: `Done! **${proposal.name}** has been created (disabled — review it before enabling). [→ Open Workflows](/workflows)`
            }
          ]);
        } else {
          setError("Could not create the workflow. Please try again.");
        }
      } catch {
        setError("Connection error while creating workflow.");
      } finally {
        setProposalApplying(null);
      }
    },
    [api]
  );

  const handleCopySteps = useCallback(
    async (steps: string[], msgIndex: number) => {
      const text = steps.map((s, i) => `${i + 1}. ${s}`).join("\n");
      await copyToClipboard(text);
      setCopiedIdx(msgIndex);
      setTimeout(() => setCopiedIdx(null), 2500);
    },
    []
  );

  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) return;
    setSubmittingFeedback(true);
    try {
      await api.admin.post("Copilot/Feedback", {
        feedbackText: feedbackText.trim(),
        sessionSummary: buildSessionSummary(),
        triggerSource: "user_submitted",
        pageContext: context
      });
      setFeedbackSent(true);
      setFeedbackText("");
      setShowFeedbackForm(false);
      setShowFrustrationBanner(false);
      setFeedbackDismissed(true);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Thanks for the feedback — our team will review it and improve Moshky. Is there anything else I can help with?"
        }
      ]);
    } catch {
      setError("Could not send feedback. Please try again.");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleSubmitTicket = async () => {
    if (!ticketForm.subject.trim()) return;
    setSubmittingTicket(true);
    try {
      const res = await api.admin.post("Copilot/Ticket", {
        ...ticketForm,
        sessionSummary: buildSessionSummary()
      });
      const data = await res.json();
      setTicketResult({ ticketNumber: data.ticketNumber, id: data.id });
      setEscalateStep("idle");

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Your support ticket has been created: **${data.ticketNumber}**\n\nWe'll reach out to **${ticketForm.contactEmail || "you"}** within 1 business day. Is there anything else I can help you with?`
        }
      ]);
    } catch {
      setError("Could not create ticket. Please email support@velaro.com.");
    } finally {
      setSubmittingTicket(false);
    }
  };

  const triggerEscalation = useCallback(async () => {
    setEscalateStep("searching");
    setEscalateSuggestion(null);
    const subject =
      messages.find((m) => m.role === "user")?.content?.slice(0, 120) ??
      "your issue";
    setTicketForm((f) => ({
      ...f,
      subject: subject,
      description: buildSessionSummary()
    }));
    try {
      const escalationMessages: ChatMessage[] = [
        ...messages,
        {
          role: "user",
          content:
            "I'm having trouble and need more help. Please review our conversation, provide your best answer, and reference any relevant help articles. Be warm and helpful."
        }
      ];
      const res = await api.admin.post("Copilot/Chat", {
        messages: escalationMessages,
        context: context + integrationLogContext
      });
      const data = await res.json();
      setEscalateSuggestion(
        data?.reply ??
          "I wasn't able to find a specific article, but our support team is ready to help."
      );
    } catch {
      setEscalateSuggestion(
        "I wasn't able to search right now, but our support team will get back to you quickly."
      );
    }
    setEscalateStep("suggested");
  }, [messages, context, api, buildSessionSummary]);

  // "Talk to a human" — check if a Velaro support agent is online, then open
  // the live chat widget (site 1032, any group). Falls back to ticket form.
  const handleTalkToHuman = useCallback(async () => {
    try {
      const res = await api.admin.get("Copilot/AgentAvailability");
      const data = await res.json();
      if (data?.available) {
        // Dynamically inject the Velaro chat widget for site 1032
        const existing = document.getElementById("velaro-support-widget");
        if (!existing) {
          const s = document.createElement("script");
          s.id = "velaro-support-widget";
          s.src =
            "https://cdn.velaro.com/js/visitor.ashx?siteid=1032&groupid=0";
          s.async = true;
          document.body.appendChild(s);
        }
        // Collapse the Copilot panel so the widget is visible
        setEscalateStep("idle");
        return;
      }
    } catch {
      /* fall through to ticket */
    }
    // No agents available — show friendly message then go to ticket
    setEscalateSuggestion(
      "Hey! No one from our team is available right now, but don't worry — let's create a support ticket instead and we'll get back to you within 1 business day."
    );
    setEscalateStep("ticket");
  }, [api]);

  const handleRequestAccess = async (featureName: string) => {
    setSendingUpgrade(featureName);
    try {
      await api.admin.post("Copilot/UpgradeRequest", {
        feature: featureName,
        notes: buildSessionSummary()
      });
      setUpgradeSent(featureName);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Your request to enable **${featureName}** has been sent to the Velaro team. We'll follow up within 1 business day. In the meantime, is there anything else I can help you with?`
        }
      ]);
    } catch {
      setError("Could not send request. Please email sales@velaro.com.");
    } finally {
      setSendingUpgrade(null);
    }
  };

  const handleSubmitRating = async () => {
    if (!ratingValue) return;
    setSendingRating(true);
    try {
      await api.admin.post("Copilot/Rating", {
        rating: ratingValue,
        comment: ratingComment.trim(),
        sessionSummary: buildSessionSummary()
      });
      setRatingSent(true);
      setShowRating(false);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Thanks for the feedback! Your input helps us improve the Copilot experience."
        }
      ]);
    } catch {
      /* silent */
    } finally {
      setSendingRating(false);
    }
  };

  // Inline rating: submit then replace the rating-marker message with a thank-you bubble.
  const handleSubmitInlineRating = async (msgIndex: number) => {
    if (!ratingValue) return;
    setSendingRating(true);
    try {
      await api.admin.post("Copilot/Rating", {
        rating: ratingValue,
        comment: ratingComment.trim(),
        sessionSummary: buildSessionSummary()
      });
      setRatingSent(true);
      setMessages((prev) =>
        prev.map((m, i) =>
          i === msgIndex
            ? {
                ...m,
                showRating: false,
                ratingDismissed: true,
                content:
                  "Thanks for the feedback! Your input helps us improve Moshky."
              }
            : m
        )
      );
      setRatingValue(0);
      setRatingComment("");
    } catch {
      /* silent */
    } finally {
      setSendingRating(false);
    }
  };

  // Inline rating: skip removes the marker message entirely so it doesn't clutter the chat.
  const handleSkipInlineRating = (msgIndex: number) => {
    setRatingSent(true);
    setMessages((prev) => prev.filter((_, i) => i !== msgIndex));
  };

  const handleEmailResponse = async (
    content: string,
    overrideEmail?: string
  ) => {
    try {
      const payload: any = { responseContent: content };
      const emailToUse = overrideEmail ?? preferredEmail;
      if (emailToUse) payload.recipientEmail = emailToUse;
      const res = await api.admin.post("Copilot/EmailResponse", payload);
      const data = await res.json();
      if (data?.success) {
        const sentTo = data.email ?? emailToUse ?? "your account email";
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `Response sent to **${sentTo}**. Not the right email? Just say "send to myemail@example.com" and I'll remember it for next time.`
          }
        ]);
      } else {
        setError(data?.message ?? "Could not send email.");
      }
    } catch {
      setError("Could not send email. Please try again.");
    }
  };

  const handleSetPreferredEmail = (email: string) => {
    const trimmed = email.trim();
    if (trimmed) {
      setPreferredEmail(trimmed);
      try {
        localStorage.setItem("copilot-preferred-email", trimmed);
      } catch {
        /* ignore */
      }
    }
    setShowEmailPrompt(false);
    if (pendingEmailContent) {
      handleEmailResponse(pendingEmailContent, trimmed);
      setPendingEmailContent(null);
    }
  };

  const handleCopy = async (text: string, idx: number) => {
    try {
      await copyToClipboard(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    } catch {
      /* clipboard API may fail in non-secure contexts */
    }
  };

  const handleNewChat = () => {
    setMessages([
      {
        role: "assistant",
        content:
          "Hey there! I'm **Moshky** — a curious little monkey who grew up right here in Velaro's server room, watching every workflow, integration, and setting get built from day one.\n\nAsk me anything about the platform and I'll help you figure it out!"
      }
    ]);
    setError(null);
    setTicketResult(null);
    setFeedbackSent(false);
    setShowRating(false);
    setRatingSent(false);
    messageCountSinceRating.current = 0;
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      /* ignore */
    }
  };

  const suggestions = buildSuggestions(subStatus?.enabledFeatures);

  // Theme classes
  const isDark = theme === "dark";
  const bg = isDark ? "bg-zinc-900" : "bg-white";
  const bgHeader = isDark ? "bg-zinc-800" : "bg-zinc-100";
  const border = isDark ? "border-zinc-700" : "border-zinc-200";
  const textPrimary = isDark ? "text-white" : "text-zinc-900";
  const textSecondary = isDark ? "text-zinc-400" : "text-zinc-500";
  const textMuted = isDark ? "text-zinc-500" : "text-zinc-400";
  const bgInput = isDark ? "bg-zinc-700" : "bg-zinc-100";
  const borderInput = isDark ? "border-zinc-600" : "border-zinc-300";
  const bgBubbleAssistant = isDark
    ? "bg-zinc-800 text-zinc-200"
    : "bg-zinc-100 text-zinc-800";
  const bgBubbleUser = "bg-blue-600 text-white";

  const activeView = showFeedbackForm
    ? "feedback"
    : showRating
      ? "rating"
      : escalateStep !== "idle"
        ? "escalate"
        : "chat";

  return (
    <div
      ref={panelRef}
      style={
        expanded
          ? undefined
          : {
              transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
              width: panelSize.w,
              height: panelSize.h
            }
      }
      className={`fixed z-50 flex flex-col shadow-2xl rounded-2xl overflow-hidden border ${border} ${bg} ${
        expanded ? "inset-4" : "bottom-20 right-5 max-w-[calc(100vw-24px)]"
      }`}
    >
      {/* Header — blue, draggable */}
      <div
        className="flex items-center justify-between px-4 py-3.5 bg-cornflower-blue-600 border-b border-cornflower-blue-700 shrink-0 cursor-grab active:cursor-grabbing select-none touch-none rounded-t-2xl"
        onPointerDown={onHeaderPointerDown}
        onPointerMove={onHeaderPointerMove}
        onPointerUp={onHeaderPointerUp}
        onPointerCancel={onHeaderPointerUp}
      >
        {/* Left: logo + title */}
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-cornflower-blue-700/60 flex items-center justify-center shrink-0">
            <svg
              width="18"
              height="15"
              viewBox="0 0 40 34"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M19.3703 13.4165C20.0828 13.3996 20.7841 13.5954 21.3848 13.979C21.9854 14.3625 22.4581 14.9164 22.7425 15.5699C23.0269 16.2233 23.1101 16.9467 22.9814 17.6477C22.8528 18.3487 22.5181 18.9954 22.0202 19.5052C21.5223 20.0151 20.8837 20.365 20.186 20.5102C19.4883 20.6555 18.7631 20.5895 18.1031 20.3207C17.4431 20.0519 16.8781 19.5924 16.4804 19.001C16.0827 18.4097 15.8703 17.7132 15.8703 17.0005C15.8599 16.0613 16.2227 15.1564 16.8789 14.4844C17.5351 13.8124 18.4312 13.4284 19.3703 13.4165Z"
                fill="white"
              />
              <path
                d="M16.5022 33.5695C-3.49176 19.6415 5.20024 10.5985 18.7612 10.0495C24.8982 9.8055 25.0852 -0.350501 18.5192 0.480499C12.0452 1.3095 -1.93776 6.7725 0.636237 16.7095C2.59324 24.2635 11.8612 31.9195 16.5022 33.5695Z"
                fill="white"
              />
              <path
                d="M22.2475 0.431499C42.2415 14.3585 33.5475 23.4025 19.9885 23.9505C13.8515 24.1955 13.6645 34.3555 20.2295 33.5195C26.7055 32.6915 40.6885 27.2315 38.1145 17.2915C36.1575 9.7375 26.8895 2.0815 22.2475 0.431499Z"
                fill="white"
              />
            </svg>
          </div>
          <div>
            <div className="text-white font-semibold text-sm leading-tight">
              Velaro AI — Moshky
            </div>
            <div className="text-cornflower-blue-200 text-[10px] leading-tight mt-0.5">
              Your AI Assistant
            </div>
          </div>
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-1">
          {activeView === "chat" && !ticketResult && (
            <>
              <IconButton
                onClick={handleNewChat}
                variant="ghost"
                size="sm"
                className="!text-cornflower-blue-200 hover:!text-white hover:!bg-cornflower-blue-700"
                aria-label="New chat"
                title="New chat"
              >
                <Plus className="w-3 h-3" />
              </IconButton>
              <GhostButton
                onClick={() => setShowFeedbackForm(true)}
                className="!no-underline !text-xs !text-cornflower-blue-200 hover:!text-white px-2 py-1 rounded hover:!bg-cornflower-blue-700"
                title="Send feedback"
              >
                <MessageSquare className="w-3 h-3" />
                Feedback
              </GhostButton>
              <IconButton
                onClick={() => triggerEscalation()}
                variant="ghost"
                size="sm"
                className="!text-cornflower-blue-200 hover:!text-white hover:!bg-cornflower-blue-700"
                aria-label="Get help from our team"
                title="Get help from our team"
              >
                <HelpCircle className="w-3 h-3" />
              </IconButton>
            </>
          )}
          <IconButton
            onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
            variant="ghost"
            size="sm"
            className="!text-cornflower-blue-200 hover:!text-white hover:!bg-cornflower-blue-700"
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            title={isDark ? "Light mode" : "Dark mode"}
          >
            <Lightbulb className="w-3 h-3" />
          </IconButton>
          <IconButton
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="!text-cornflower-blue-200 hover:!text-white hover:!bg-cornflower-blue-700"
            aria-label="Close"
            title="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </IconButton>
        </div>
      </div>

      {
        <>
          {/* Feedback form */}
          {activeView === "feedback" && (
            <div className={`flex-1 overflow-y-auto p-4 ${bg}`}>
              <h3 className={`${textPrimary} font-semibold mb-2`}>
                Send Feedback
              </h3>
              <p className={`${textSecondary} text-sm mb-4`}>
                Tell us what you couldn't do, or where Moshky got it wrong. Your
                feedback goes directly to our product team.
              </p>
              <textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                rows={6}
                placeholder="What were you trying to do? What went wrong or wasn't helpful?"
                className={`w-full ${bgInput} border ${borderInput} rounded px-3 py-2 ${textPrimary} text-sm placeholder-${isDark ? "zinc-500" : "gray-400"} focus:outline-none focus:border-blue-500 resize-none`}
              />
              {error && (
                <div
                  className={`mt-3 text-sm ${isDark ? "text-red-400 bg-red-900/30 border-red-700" : "text-red-600 bg-red-50 border-red-300"} border rounded p-2`}
                >
                  {error}
                </div>
              )}
              <div className="flex gap-2 mt-4">
                <PrimaryButton
                  onClick={handleSubmitFeedback}
                  disabled={submittingFeedback || !feedbackText.trim()}
                  className={`!flex-1 !bg-blue-600 hover:!bg-blue-700 disabled:!opacity-100 ${isDark ? "disabled:!bg-zinc-700 disabled:!text-zinc-500" : "disabled:!bg-zinc-200 disabled:!text-zinc-400"} !rounded`}
                >
                  {submittingFeedback ? (
                    "Sending..."
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5 inline mr-1.5" />
                      Send Feedback
                    </>
                  )}
                </PrimaryButton>
                <GhostButton
                  onClick={() => {
                    setShowFeedbackForm(false);
                    setShowFrustrationBanner(false);
                  }}
                  className={`!no-underline px-4 py-2 ${textSecondary.replace("text-", "!text-")} hover:${textPrimary.replace("text-", "!text-")} ${isDark ? "hover:bg-zinc-700" : "hover:bg-zinc-200"} rounded`}
                >
                  Cancel
                </GhostButton>
              </div>
              <p className={`${textMuted} text-xs mt-3`}>
                No email required — we'll use your account info. For urgent
                issues, use "Create Ticket" instead.
              </p>
            </div>
          )}

          {/* NPS Rating prompt */}
          {activeView === "rating" && (
            <div
              className={`flex-1 overflow-y-auto p-4 ${bg} flex flex-col items-center justify-center gap-4`}
            >
              <div
                className={`w-12 h-12 rounded-full ${isDark ? "bg-blue-900/40 border-blue-700" : "bg-blue-100 border-blue-200"} border flex items-center justify-center`}
              >
                <Star className="w-5 h-5 text-blue-400" />
              </div>
              <div className="text-center">
                <h3 className={`${textPrimary} font-semibold mb-1`}>
                  How's your experience?
                </h3>
                <p className={`${textSecondary} text-sm`}>
                  Your feedback helps us improve. What would make this better?
                </p>
              </div>
              {/* Left native: star-rating picker, a segmented selector, not a single action. */}
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRatingValue(star)}
                    className={`w-10 h-10 rounded-lg border transition-colors text-lg ${
                      star <= ratingValue
                        ? "bg-yellow-500 border-yellow-400 text-white"
                        : isDark
                          ? "bg-zinc-800 border-zinc-600 text-zinc-500 hover:border-yellow-500 hover:text-yellow-400"
                          : "bg-zinc-100 border-zinc-300 text-zinc-400 hover:border-yellow-500 hover:text-yellow-400"
                    }`}
                    aria-label={`${star} star${star > 1 ? "s" : ""}`}
                  >
                    <Star className="w-4 h-4" />
                  </button>
                ))}
              </div>
              <textarea
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                rows={3}
                placeholder="What would make your experience better? (optional)"
                className={`w-full ${bgInput} border ${borderInput} rounded px-3 py-2 ${textPrimary} text-sm focus:outline-none focus:border-blue-500 resize-none`}
              />
              <div className="flex gap-2 w-full">
                <PrimaryButton
                  onClick={handleSubmitRating}
                  disabled={sendingRating || !ratingValue}
                  className="!flex-1 !bg-blue-600 hover:!bg-blue-700 disabled:!opacity-40 !rounded"
                >
                  {sendingRating ? "Sending..." : "Submit"}
                </PrimaryButton>
                <GhostButton
                  onClick={() => {
                    setShowRating(false);
                    setRatingSent(true);
                  }}
                  className={`!no-underline px-4 py-2 ${textSecondary.replace("text-", "!text-")} hover:${textPrimary.replace("text-", "!text-")} ${isDark ? "hover:bg-zinc-700" : "hover:bg-zinc-200"} rounded`}
                >
                  Skip
                </GhostButton>
              </div>
            </div>
          )}

          {/* Escalation flow */}
          {activeView === "escalate" && (
            <div
              className={`flex-1 overflow-y-auto p-4 ${bg} flex flex-col gap-4`}
            >
              {/* Step: searching */}
              {escalateStep === "searching" && (
                <div className="flex flex-col items-center justify-center flex-1 gap-3 text-center">
                  <div
                    className={`w-10 h-10 rounded-full ${isDark ? "bg-blue-900/40 border-blue-700" : "bg-blue-50 border-blue-200"} border flex items-center justify-center`}
                  >
                    <Search className="w-4 h-4 text-blue-500 animate-pulse" />
                  </div>
                  <p className={`${textPrimary} text-sm font-medium`}>
                    Looking up help articles for you…
                  </p>
                  <p className={`${textSecondary} text-xs`}>
                    Searching our knowledge base
                  </p>
                </div>
              )}

              {/* Step: suggested — AI response + action buttons */}
              {(escalateStep === "suggested" || escalateStep === "ticket") &&
                escalateSuggestion && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div
                        className={`w-6 h-6 rounded-lg ${isDark ? "bg-zinc-700" : "bg-zinc-200"} flex items-center justify-center shrink-0`}
                      >
                        <svg
                          width="12"
                          height="10"
                          viewBox="0 0 40 34"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M19.3703 13.4165C20.0828 13.3996 20.7841 13.5954 21.3848 13.979C21.9854 14.3625 22.4581 14.9164 22.7425 15.5699C23.0269 16.2233 23.1101 16.9467 22.9814 17.6477C22.8528 18.3487 22.5181 18.9954 22.0202 19.5052C21.5223 20.0151 20.8837 20.365 20.186 20.5102C19.4883 20.6555 18.7631 20.5895 18.1031 20.3207C17.4431 20.0519 16.8781 19.5924 16.4804 19.001C16.0827 18.4097 15.8703 17.7132 15.8703 17.0005C15.8599 16.0613 16.2227 15.1564 16.8789 14.4844C17.5351 13.8124 18.4312 13.4284 19.3703 13.4165Z"
                            fill={isDark ? "white" : "#374151"}
                          />
                          <path
                            d="M16.5022 33.5695C-3.49176 19.6415 5.20024 10.5985 18.7612 10.0495C24.8982 9.8055 25.0852 -0.350501 18.5192 0.480499C12.0452 1.3095 -1.93776 6.7725 0.636237 16.7095C2.59324 24.2635 11.8612 31.9195 16.5022 33.5695Z"
                            fill={isDark ? "white" : "#374151"}
                          />
                          <path
                            d="M22.2475 0.431499C42.2415 14.3585 33.5475 23.4025 19.9885 23.9505C13.8515 24.1955 13.6645 34.3555 20.2295 33.5195C26.7055 32.6915 40.6885 27.2315 38.1145 17.2915C36.1575 9.7375 26.8895 2.0815 22.2475 0.431499Z"
                            fill={isDark ? "white" : "#374151"}
                          />
                        </svg>
                      </div>
                      <span
                        className={`${isDark ? "text-cornflower-blue-300" : "text-cornflower-blue-600"} text-xs font-semibold`}
                      >
                        Moshky
                      </span>
                    </div>
                    <div
                      className={`${bgBubbleAssistant} rounded-2xl rounded-bl-sm px-4 py-3 text-sm leading-relaxed mb-4`}
                      dangerouslySetInnerHTML={{
                        __html: renderMarkdown(escalateSuggestion, isDark)
                      }}
                    />
                  </div>
                )}

              {/* Step: suggested — action buttons */}
              {/* Left native: 3-way choice list (Yes/Create ticket/Talk to human), each with
                  its own full-width row layout + unique semantic color — a selection group, not
                  a single-purpose action button, same exception as suggestion-chip pickers. */}
              {escalateStep === "suggested" && (
                <div className="flex flex-col gap-3">
                  <p className={`${textSecondary} text-sm mb-1`}>
                    Did this help?
                  </p>
                  <button
                    onClick={() => {
                      setEscalateStep("idle");
                    }}
                    className={`w-full ${isDark ? "bg-green-700/40 hover:bg-green-700/60 border-green-700/60 text-green-300" : "bg-green-50 hover:bg-green-100 border-green-300 text-green-700"} border rounded-lg px-4 py-3 text-sm font-medium transition-colors text-left flex items-center gap-3`}
                  >
                    <Check
                      className={`w-4 h-4 ${isDark ? "text-green-400" : "text-green-600"}`}
                    />
                    Yes, this answered my question
                  </button>
                  <button
                    onClick={() => setEscalateStep("ticket")}
                    className={`w-full ${isDark ? "bg-zinc-800 hover:bg-zinc-700 border-zinc-600 text-zinc-200" : "bg-zinc-50 hover:bg-zinc-100 border-zinc-300 text-zinc-700"} border rounded-lg px-4 py-3 text-sm font-medium transition-colors text-left flex items-center gap-3`}
                  >
                    <HelpCircle
                      className={`w-4 h-4 ${isDark ? "text-zinc-400" : "text-zinc-500"}`}
                    />
                    Create a support ticket
                  </button>
                  <button
                    onClick={handleTalkToHuman}
                    className={`w-full ${isDark ? "bg-blue-900/30 hover:bg-blue-900/50 border-blue-700/50 text-blue-300" : "bg-blue-50 hover:bg-blue-100 border-blue-300 text-blue-700"} border rounded-lg px-4 py-3 text-sm font-medium transition-colors text-left flex items-center gap-3`}
                  >
                    <User
                      className={`w-4 h-4 ${isDark ? "text-blue-400" : "text-blue-600"}`}
                    />
                    Talk to a human
                  </button>
                </div>
              )}

              {/* Step: ticket form — streamlined */}
              {escalateStep === "ticket" && (
                <div className="space-y-3">
                  <p className={`${textSecondary} text-sm`}>
                    We'll respond within 1 business day. Your conversation will
                    be attached automatically.
                  </p>
                  <div>
                    <label className={`${textPrimary} text-xs mb-1 block`}>
                      Subject
                    </label>
                    <input
                      type="text"
                      value={ticketForm.subject}
                      onChange={(e) =>
                        setTicketForm((f) => ({
                          ...f,
                          subject: e.target.value
                        }))
                      }
                      placeholder="What do you need help with?"
                      className={`w-full ${bgInput} border ${borderInput} rounded px-3 py-2 ${textPrimary} text-sm ${isDark ? "placeholder-zinc-500" : "placeholder-zinc-400"} focus:outline-none focus:border-blue-500`}
                    />
                  </div>
                  <div>
                    <label className={`${textPrimary} text-xs mb-1 block`}>
                      Reply email
                    </label>
                    <input
                      type="email"
                      value={ticketForm.contactEmail}
                      onChange={(e) =>
                        setTicketForm((f) => ({
                          ...f,
                          contactEmail: e.target.value
                        }))
                      }
                      placeholder="your@email.com"
                      className={`w-full ${bgInput} border ${borderInput} rounded px-3 py-2 ${textPrimary} text-sm ${isDark ? "placeholder-zinc-500" : "placeholder-zinc-400"} focus:outline-none focus:border-blue-500`}
                    />
                  </div>
                  {error && (
                    <div
                      className={`text-sm ${isDark ? "text-red-400 bg-red-900/30 border-red-700" : "text-red-600 bg-red-50 border-red-300"} border rounded p-2`}
                    >
                      {error}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <PrimaryButton
                      onClick={handleSubmitTicket}
                      disabled={submittingTicket || !ticketForm.subject.trim()}
                      className={`!flex-1 !bg-blue-600 hover:!bg-blue-700 disabled:!opacity-100 ${isDark ? "disabled:!bg-zinc-700 disabled:!text-zinc-500" : "disabled:!bg-zinc-200 disabled:!text-zinc-400"} !rounded`}
                    >
                      {submittingTicket ? (
                        "Submitting…"
                      ) : (
                        <>
                          <HelpCircle className="w-3.5 h-3.5 inline mr-1.5" />
                          Submit Ticket
                        </>
                      )}
                    </PrimaryButton>
                    <GhostButton
                      onClick={() => setEscalateStep("suggested")}
                      className={`!no-underline px-4 py-2 ${textSecondary.replace("text-", "!text-")} hover:${textPrimary.replace("text-", "!text-")} ${isDark ? "hover:bg-zinc-700" : "hover:bg-zinc-200"} rounded`}
                    >
                      Back
                    </GhostButton>
                  </div>
                </div>
              )}

              {/* Cancel always visible */}
              {escalateStep !== "searching" && (
                <GhostButton
                  onClick={() => setEscalateStep("idle")}
                  className={`!no-underline !text-xs ${textSecondary.replace("text-", "!text-")} hover:${textPrimary.replace("text-", "!text-")} text-center mt-auto`}
                >
                  ← Back to chat
                </GhostButton>
              )}
            </div>
          )}

          {/* Chat messages */}
          {activeView === "chat" && (
            <>
              <div
                className={`flex-1 overflow-y-auto p-4 space-y-4 min-h-0 ${bg}`}
              >
                {/* Suggestion chips + upgrade prompts — only shown before user sends first message */}
                {/* Left native: suggestion-chip picker, per CLAUDE.md's ds/ table exception for
                    segmented/multi-option button groups. */}
                {messages.length === 1 && (
                  <>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {suggestions.map((s) => (
                        <button
                          key={s.label}
                          onClick={() => sendMessage(s.query)}
                          className={`text-xs ${isDark ? "bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white border-zinc-600" : "bg-zinc-50 hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900 border-zinc-200"} border rounded-lg px-3 py-2 transition-colors text-left inline-flex items-center gap-1.5`}
                        >
                          <s.Icon className="w-3 h-3 text-blue-400" />
                          {s.label}
                        </button>
                      ))}
                      {subStatus && subStatus.disabledFeatures.length > 0 && (
                        <button
                          onClick={() =>
                            sendMessage(
                              "What features can I unlock for my account?"
                            )
                          }
                          className={`text-xs border rounded-md px-3 py-1.5 transition-colors text-left ${
                            isDark
                              ? "bg-blue-900/40 hover:bg-blue-800/60 text-blue-300 hover:text-blue-200 border-blue-700/50"
                              : "bg-blue-50 hover:bg-blue-100 text-blue-700 hover:text-blue-800 border-blue-300"
                          }`}
                        >
                          ✦ Upgrade to unlock{" "}
                          {subStatus.disabledFeatures.length} more features
                        </button>
                      )}
                    </div>

                    {/* Near-limit warning shown at the top of the chat */}
                    {subStatus &&
                      subStatus.nearLimits.length > 0 &&
                      !nearLimitDismissed && (
                        <div className="mt-3 bg-amber-900/25 border border-amber-700/50 rounded-lg p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-amber-300 text-xs font-semibold mb-1">
                                Approaching usage limits
                              </p>
                              {subStatus.nearLimits.map((l) => (
                                <div
                                  key={l.feature}
                                  className="flex items-center gap-2 mb-1"
                                >
                                  <div className="flex-1">
                                    <div className="flex justify-between text-xs text-amber-400/80 mb-0.5">
                                      <span>{l.feature}</span>
                                      <span>
                                        {l.current} / {l.max}
                                      </span>
                                    </div>
                                    <div className="h-1 bg-zinc-700 rounded-full overflow-hidden">
                                      <div
                                        className={`h-full rounded-full ${l.percentUsed >= 90 ? "bg-red-500" : "bg-amber-500"}`}
                                        style={{
                                          width: `${Math.min(l.percentUsed, 100)}%`
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                              <p className="text-amber-500/70 text-xs mt-1">
                                Ask me about upgrading your limits.
                              </p>
                            </div>
                            <IconButton
                              onClick={() => setNearLimitDismissed(true)}
                              variant="ghost"
                              size="sm"
                              className="!w-auto !h-auto !p-0 !text-amber-600 hover:!text-amber-400 hover:!bg-transparent !text-xs !mt-0.5 shrink-0"
                              aria-label="Dismiss"
                            >
                              ✕
                            </IconButton>
                          </div>
                        </div>
                      )}
                  </>
                )}

                {messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} group`}
                  >
                    {msg.role === "assistant" && (
                      <div
                        className={`w-6 h-6 rounded-lg ${isDark ? "bg-zinc-700" : "bg-zinc-200"} flex items-center justify-center mr-2 mt-1 shrink-0`}
                      >
                        <svg
                          width="12"
                          height="10"
                          viewBox="0 0 40 34"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M19.3703 13.4165C20.0828 13.3996 20.7841 13.5954 21.3848 13.979C21.9854 14.3625 22.4581 14.9164 22.7425 15.5699C23.0269 16.2233 23.1101 16.9467 22.9814 17.6477C22.8528 18.3487 22.5181 18.9954 22.0202 19.5052C21.5223 20.0151 20.8837 20.365 20.186 20.5102C19.4883 20.6555 18.7631 20.5895 18.1031 20.3207C17.4431 20.0519 16.8781 19.5924 16.4804 19.001C16.0827 18.4097 15.8703 17.7132 15.8703 17.0005C15.8599 16.0613 16.2227 15.1564 16.8789 14.4844C17.5351 13.8124 18.4312 13.4284 19.3703 13.4165Z"
                            fill={isDark ? "white" : "#374151"}
                          />
                          <path
                            d="M16.5022 33.5695C-3.49176 19.6415 5.20024 10.5985 18.7612 10.0495C24.8982 9.8055 25.0852 -0.350501 18.5192 0.480499C12.0452 1.3095 -1.93776 6.7725 0.636237 16.7095C2.59324 24.2635 11.8612 31.9195 16.5022 33.5695Z"
                            fill={isDark ? "white" : "#374151"}
                          />
                          <path
                            d="M22.2475 0.431499C42.2415 14.3585 33.5475 23.4025 19.9885 23.9505C13.8515 24.1955 13.6645 34.3555 20.2295 33.5195C26.7055 32.6915 40.6885 27.2315 38.1145 17.2915C36.1575 9.7375 26.8895 2.0815 22.2475 0.431499Z"
                            fill={isDark ? "white" : "#374151"}
                          />
                        </svg>
                      </div>
                    )}
                    <div className="max-w-[85%] flex flex-col">
                      {msg.role === "assistant" && msg.showRating ? (
                        <div
                          className={`${bgBubbleAssistant} rounded-2xl rounded-bl-sm px-4 py-3`}
                        >
                          <div
                            className={`text-xs font-semibold ${textPrimary} mb-2`}
                          >
                            How's it going so far?
                          </div>
                          {/* Left native: star-rating picker, a segmented selector, not a single action. */}
                          <div className="flex gap-1.5 mb-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                onClick={() => setRatingValue(star)}
                                className={`w-7 h-7 rounded-md border transition-colors text-sm ${
                                  star <= ratingValue
                                    ? "bg-yellow-500 border-yellow-400 text-white"
                                    : isDark
                                      ? "bg-zinc-800 border-zinc-600 text-zinc-500 hover:border-yellow-500 hover:text-yellow-400"
                                      : "bg-white border-zinc-300 text-zinc-400 hover:border-yellow-500 hover:text-yellow-400"
                                }`}
                                aria-label={`${star} star${star > 1 ? "s" : ""}`}
                              >
                                <Star className="w-4 h-4" />
                              </button>
                            ))}
                          </div>
                          <textarea
                            value={ratingComment}
                            onChange={(e) => setRatingComment(e.target.value)}
                            rows={2}
                            placeholder="Optional: anything we can improve?"
                            className={`w-full ${bgInput} border ${borderInput} rounded px-2 py-1.5 ${textPrimary} text-xs focus:outline-none focus:border-blue-500 resize-none mb-2`}
                          />
                          <div className="flex gap-2">
                            <PrimaryButton
                              onClick={() => handleSubmitInlineRating(i)}
                              disabled={sendingRating || !ratingValue}
                              className="!bg-blue-600 hover:!bg-blue-700 disabled:!opacity-40 !rounded !px-3 !py-1 !text-xs"
                            >
                              {sendingRating ? "Sending…" : "Submit"}
                            </PrimaryButton>
                            <GhostButton
                              onClick={() => handleSkipInlineRating(i)}
                              className={`!no-underline !text-xs ${textSecondary.replace("text-", "!text-")} hover:${textPrimary.replace("text-", "!text-")} px-2 py-1 rounded`}
                            >
                              Skip
                            </GhostButton>
                          </div>
                        </div>
                      ) : (
                        <div
                          className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                            msg.role === "user"
                              ? `${bgBubbleUser} rounded-br-sm`
                              : `${bgBubbleAssistant} rounded-bl-sm`
                          }`}
                        >
                          {msg.role === "assistant" ? (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: renderMarkdown(msg.content, isDark)
                              }}
                              onClick={(e) => {
                                const a = (e.target as HTMLElement).closest(
                                  "[data-nav]"
                                );
                                if (a) {
                                  e.preventDefault();
                                  navigate(a.getAttribute("data-nav") ?? "/");
                                }
                              }}
                            />
                          ) : (
                            <span>{msg.content}</span>
                          )}
                        </div>
                      )}
                      {/* Workflow proposal card — shown inline below assistant bubble */}
                      {msg.role === "assistant" && msg.proposal && (
                        <WorkflowProposalCard
                          proposal={msg.proposal}
                          isDark={isDark}
                          msgIndex={i}
                          onApply={handleApplyProposal}
                          onCopySteps={handleCopySteps}
                          applying={proposalApplying === i}
                          applied={proposalApplied.has(i)}
                        />
                      )}
                      {/* Action buttons — copy + email (assistant messages only, not on rating prompts) */}
                      {msg.role === "assistant" && i > 0 && !msg.showRating && (
                        <div className="flex gap-1.5 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <SecondaryButton
                            onClick={() => handleCopy(msg.content, i)}
                            className={`!text-xs ${isDark ? "!text-zinc-400 hover:!text-zinc-200 hover:!bg-zinc-700" : "!text-zinc-500 hover:!text-zinc-700 hover:!bg-zinc-100"} !bg-transparent !px-2.5 !py-1 !rounded-md !gap-1.5 border ${isDark ? "!border-zinc-600" : "!border-zinc-300"}`}
                            title="Copy response"
                            aria-label="Copy response"
                          >
                            {copiedIdx === i ? (
                              <Check className="w-3 h-3" />
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                            {copiedIdx === i ? "Copied" : "Copy"}
                          </SecondaryButton>
                          <SecondaryButton
                            onClick={() => handleEmailResponse(msg.content)}
                            className={`!text-xs ${isDark ? "!text-zinc-400 hover:!text-zinc-200 hover:!bg-zinc-700" : "!text-zinc-500 hover:!text-zinc-700 hover:!bg-zinc-100"} !bg-transparent !px-2.5 !py-1 !rounded-md !gap-1.5 border ${isDark ? "!border-zinc-600" : "!border-zinc-300"}`}
                            title="Email this response to me"
                            aria-label="Email response"
                          >
                            <Mail className="w-3 h-3" />
                            Email to me
                          </SecondaryButton>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Quick-reply chips — only on the last assistant message, only when not loading */}
                {/* Left native: suggestion-chip picker, same exception as the chips above. */}
                {!loading &&
                  (() => {
                    const last = messages[messages.length - 1];
                    if (!last || last.role !== "assistant" || last.showRating)
                      return null;
                    const chips = detectQuickReplies(last.content);
                    if (chips.length === 0) return null;
                    return (
                      <div className="flex flex-wrap gap-2 ml-8 -mt-2">
                        {chips.map((chip) => (
                          <button
                            key={chip}
                            onClick={() => sendMessage(chip)}
                            className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                              isDark
                                ? "bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border-zinc-600 hover:border-blue-500"
                                : "bg-white hover:bg-zinc-50 text-zinc-700 border-zinc-300 hover:border-blue-500 hover:text-blue-700"
                            }`}
                          >
                            {chip}
                          </button>
                        ))}
                      </div>
                    );
                  })()}

                {/* Typing indicator — centered with logo above dots */}
                {loading && (
                  <div className="flex flex-col items-center gap-2 py-4">
                    <div
                      className={`w-8 h-8 rounded-lg ${isDark ? "bg-zinc-700" : "bg-zinc-200"} flex items-center justify-center shrink-0`}
                    >
                      <svg
                        width="16"
                        height="14"
                        viewBox="0 0 40 34"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M16.5022 33.5695C-3.49176 19.6415 5.20024 10.5985 18.7612 10.0495C24.8982 9.8055 25.0852 -0.350501 18.5192 0.480499C12.0452 1.3095 -1.93776 6.7725 0.636237 16.7095C2.59324 24.2635 11.8612 31.9195 16.5022 33.5695Z"
                          fill={isDark ? "white" : "#374151"}
                        />
                        <path
                          d="M22.2475 0.431499C42.2415 14.3585 33.5475 23.4025 19.9885 23.9505C13.8515 24.1955 13.6645 34.3555 20.2295 33.5195C26.7055 32.6915 40.6885 27.2315 38.1145 17.2915C36.1575 9.7375 26.8895 2.0815 22.2475 0.431499Z"
                          fill={isDark ? "white" : "#374151"}
                        />
                      </svg>
                    </div>
                    <span
                      className={`inline-flex gap-1.5 ${textSecondary} text-xl leading-none`}
                    >
                      <span
                        className="animate-bounce"
                        style={{ animationDelay: "0ms" }}
                      >
                        •
                      </span>
                      <span
                        className="animate-bounce"
                        style={{ animationDelay: "150ms" }}
                      >
                        •
                      </span>
                      <span
                        className="animate-bounce"
                        style={{ animationDelay: "300ms" }}
                      >
                        •
                      </span>
                    </span>
                  </div>
                )}

                {/* Ticket result banner */}
                {ticketResult && (
                  <div
                    className={`${isDark ? "bg-green-900/40 border-green-700" : "bg-green-50 border-green-300"} border rounded-lg p-3 text-sm`}
                  >
                    <div
                      className={`${isDark ? "text-green-400" : "text-green-700"} font-semibold mb-1`}
                    >
                      Ticket Created: {ticketResult.ticketNumber}
                    </div>
                    <div className={textSecondary}>
                      We'll follow up within 1 business day.
                    </div>
                  </div>
                )}

                {/* Feedback sent confirmation */}
                {feedbackSent && (
                  <div
                    className={`${isDark ? "bg-blue-900/30 border-blue-700 text-blue-300" : "bg-blue-50 border-blue-300 text-blue-700"} border rounded-lg p-3 text-sm`}
                  >
                    Feedback received — thank you for helping us improve Moshky!
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Frustration detected banner */}
              {showFrustrationBanner && !feedbackDismissed && (
                <div className="px-4 py-3 bg-amber-900/30 border-t border-amber-700 flex items-start gap-3 shrink-0">
                  <span className="text-amber-400 text-lg leading-none mt-0.5">
                    !
                  </span>
                  <div className="flex-1">
                    <p className="text-amber-300 text-xs font-semibold">
                      Not getting what you need?
                    </p>
                    <p className="text-amber-400/80 text-xs mt-0.5">
                      Tell us what's missing and we'll improve Moshky.
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <PrimaryButton
                      onClick={() => setShowFeedbackForm(true)}
                      className="!text-xs !bg-amber-600 hover:!bg-amber-700 !rounded !px-2 !py-1 whitespace-nowrap"
                    >
                      Send Feedback
                    </PrimaryButton>
                    <GhostButton
                      onClick={() => {
                        setShowFrustrationBanner(false);
                        setFeedbackDismissed(true);
                      }}
                      className="!no-underline !text-xs !text-amber-500 hover:!text-amber-300 text-center"
                    >
                      Dismiss
                    </GhostButton>
                  </div>
                </div>
              )}

              {/* Error banner */}
              {error && (
                <div
                  className={`px-4 py-2 ${isDark ? "bg-red-900/30 border-red-800 text-red-400" : "bg-red-50 border-red-200 text-red-600"} border-t text-xs flex items-center justify-between`}
                >
                  <span>{error}</span>
                  <IconButton
                    onClick={() => setError(null)}
                    variant="ghost"
                    size="sm"
                    aria-label="Dismiss error"
                    className={`!w-auto !h-auto !p-0 !ml-2 hover:!bg-transparent ${isDark ? "!text-red-400 hover:!text-white" : "!text-red-500 hover:!text-red-700"}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </IconButton>
                </div>
              )}

              {/* Input area */}
              <div
                className={`px-4 py-3 ${bgHeader} border-t ${border} shrink-0`}
              >
                <div className="flex items-end gap-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask anything about Velaro…"
                    rows={1}
                    className={`flex-1 ${bgInput} border ${borderInput} rounded-xl px-4 py-2.5 ${textPrimary} text-sm ${isDark ? "placeholder-zinc-400" : "placeholder-zinc-400"} focus:outline-none focus:border-blue-500 resize-none leading-5 max-h-32 overflow-y-auto`}
                    style={{ minHeight: "42px" }}
                    onInput={(e) => {
                      const el = e.currentTarget;
                      el.style.height = "auto";
                      el.style.height = Math.min(el.scrollHeight, 128) + "px";
                    }}
                  />
                  <IconButton
                    onClick={() => sendMessage(input)}
                    disabled={loading || !input.trim()}
                    className="!w-[42px] !h-[42px] !bg-cornflower-blue-600 hover:!bg-cornflower-blue-700 disabled:!opacity-40 !text-white !rounded-xl shrink-0"
                    aria-label="Send message"
                  >
                    <Send01 className="w-5 h-5" />
                  </IconButton>
                </div>
              </div>
            </>
          )}
        </>
      }

      {/* Resize handle — bottom-right corner, hidden in expanded mode */}
      {!expanded && (
        <div
          onPointerDown={onResizePointerDown}
          onPointerMove={onResizePointerMove}
          onPointerUp={onResizePointerUp}
          onPointerCancel={onResizePointerUp}
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize touch-none select-none"
          aria-hidden="true"
          style={{ zIndex: 10 }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            className="absolute bottom-1 right-1 text-zinc-400 opacity-50"
          >
            <path
              d="M11 1L1 11M11 6L6 11M11 11"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
      )}
    </div>
  );
}
